import { useState, useEffect, useRef, ChangeEvent, useCallback, useMemo } from 'react';
import { AssetType, Asset } from '../../types/asset';
import { assetsApi, ApiError } from '../../services/api';
import { useEditor } from '../../contexts/EditorContext';
import { validateHotbarPresets, setHotbarSlot, removeFromHotbar, HOTBAR_SIZE, HOTBAR_PRESET_COUNT, HotbarPresets } from '../../services/hotbarService';
import './AssetTab.css';

interface AssetTabProps {
  assetType: AssetType;
}

interface FolderNode {
  name: string;
  path: string;
  isOpen: boolean;
  children: FolderNode[];
}

interface DragState {
  type: 'asset' | 'folder' | null;
  id: string | null;
  sourcePath: string | null;
}

interface DropTarget {
  type: 'folder' | 'root' | 'hotbar-slot' | null;
  path: string | null;
  presetIndex?: number;
  slotIndex?: number;
}

// Local storage key for open folders state
const getOpenFoldersKey = (assetType: AssetType): string => `asset-open-folders-${assetType}`;

/**
 * Tab content for displaying and managing assets with folder organization
 * Folders are derived from asset.folder paths (e.g., "Folder1/SubFolder/Another")
 */
function AssetTab({ assetType }: AssetTabProps): JSX.Element {
  const { refreshAssets } = useEditor();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loadedAssetType, setLoadedAssetType] = useState<AssetType | null>(null);
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());
  const [hotbarPresets, setHotbarPresets] = useState<HotbarPresets>({ presets: [], activePreset: 0 });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [newAssetLabel, setNewAssetLabel] = useState<string>('');
  const [newAssetName, setNewAssetName] = useState<string>('');
  const [newAssetSize, setNewAssetSize] = useState<string>('1');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [nameManuallyEdited, setNameManuallyEdited] = useState<boolean>(false);
  const [labelManuallyEdited, setLabelManuallyEdited] = useState<boolean>(false);
  const [isAddFormVisible, setIsAddFormVisible] = useState<boolean>(false);
  const [editingFolderPath, setEditingFolderPath] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState<string>('');
  const [dragState, setDragState] = useState<DragState>({ type: null, id: null, sourcePath: null });
  const [dropTarget, setDropTarget] = useState<DropTarget>({ type: null, path: null });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Save open folders to localStorage
  const saveOpenFolders = useCallback((newOpenFolders: Set<string>) => {
    setOpenFolders(newOpenFolders);
    localStorage.setItem(getOpenFoldersKey(assetType), JSON.stringify([...newOpenFolders]));
  }, [assetType]);

  // Build folder tree from asset paths
  const folderTree = useMemo((): FolderNode[] => {
    const allPaths = new Set<string>();

    // Collect all folder paths from assets
    assets.forEach(asset => {
      if (asset.folder) {
        // Add the folder and all its parent paths
        const parts = asset.folder.split('/');
        let currentPath = '';
        parts.forEach(part => {
          currentPath = currentPath ? `${currentPath}/${part}` : part;
          allPaths.add(currentPath);
        });
      }
    });

    // Also include paths from openFolders (for empty folders)
    openFolders.forEach(path => {
      const parts = path.split('/');
      let currentPath = '';
      parts.forEach(part => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        allPaths.add(currentPath);
      });
    });

    // Build tree structure
    const buildTree = (parentPath: string | null): FolderNode[] => {
      const nodes: FolderNode[] = [];
      const pathsAtLevel = new Set<string>();

      allPaths.forEach(path => {
        const parts = path.split('/');
        if (parentPath === null) {
          // Root level folders
          if (parts.length >= 1) {
            pathsAtLevel.add(parts[0]);
          }
        } else {
          // Child folders
          const parentParts = parentPath.split('/');
          if (parts.length === parentParts.length + 1 && path.startsWith(parentPath + '/')) {
            pathsAtLevel.add(path);
          }
        }
      });

      pathsAtLevel.forEach(path => {
        const fullPath = parentPath === null ? path : path;
        const name = path.split('/').pop() || path;
        nodes.push({
          name,
          path: fullPath,
          isOpen: openFolders.has(fullPath),
          children: buildTree(fullPath)
        });
      });

      // Sort folders alphabetically
      return nodes.sort((a, b) => a.name.localeCompare(b.name));
    };

    return buildTree(null);
  }, [assets, openFolders]);

  /**
   * Fetches assets from the API and validates hotbar presets
   */
  const fetchAssets = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const response = await assetsApi.list(assetType);
      setAssets(response.assets);
      setLoadedAssetType(assetType);

      // Validate hotbar presets against actual assets to remove invalid references
      const validAssetNames = new Set(response.assets.map(a => a.name));
      const validatedPresets = validateHotbarPresets(assetType, validAssetNames);
      setHotbarPresets(validatedPresets);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load assets');
      }
    } finally {
      setIsLoading(false);
    }
  }, [assetType]);

  // Main effect: reset state and fetch assets when asset type changes
  useEffect(() => {
    // SYNCHRONOUSLY reset all state FIRST to prevent rendering with stale data
    setHotbarPresets({ presets: [], activePreset: 0 });
    setAssets([]);
    setLoadedAssetType(null);
    resetForm();
    setIsAddFormVisible(false);
    setEditingFolderPath(null);

    // Load open folders state from localStorage
    const stored = localStorage.getItem(getOpenFoldersKey(assetType));
    if (stored) {
      try {
        setOpenFolders(new Set(JSON.parse(stored)));
      } catch {
        setOpenFolders(new Set());
      }
    } else {
      setOpenFolders(new Set());
    }

    // Then fetch assets asynchronously
    fetchAssets();
  }, [assetType, fetchAssets]);

  const generateNameFromLabel = (label: string): string => {
    return label.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '').substring(0, 50);
  };

  const getFilenameWithoutExtension = (filename: string): string => {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return filename;
    return filename.substring(0, lastDot);
  };

  const handleLabelChange = (value: string): void => {
    setNewAssetLabel(value);
    setLabelManuallyEdited(true);
    if (!nameManuallyEdited) {
      setNewAssetName(generateNameFromLabel(value));
    }
  };

  const handleNameChange = (value: string): void => {
    setNameManuallyEdited(true);
    setNewAssetName(value.toLowerCase().replace(/[^a-z0-9_-]/g, ''));
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    if (file && !labelManuallyEdited) {
      const filenameWithoutExt = getFilenameWithoutExtension(file.name);
      setNewAssetLabel(filenameWithoutExt);
      if (!nameManuallyEdited) {
        setNewAssetName(generateNameFromLabel(filenameWithoutExt));
      }
    }
  };

  const resetForm = (): void => {
    setNewAssetLabel('');
    setNewAssetName('');
    setNewAssetSize('1');
    setSelectedFile(null);
    setNameManuallyEdited(false);
    setLabelManuallyEdited(false);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async (): Promise<void> => {
    if (!selectedFile || !newAssetName || !newAssetLabel) {
      setUploadError('Please fill all fields and select a file');
      return;
    }
    let sizeMeters: number | undefined;
    if (assetType === 'props') {
      const parsedSize = parseFloat(newAssetSize);
      if (isNaN(parsedSize) || parsedSize <= 0) {
        setUploadError('Size must be a positive number');
        return;
      }
      sizeMeters = parsedSize;
    }
    setIsUploading(true);
    setUploadError('');
    try {
      await assetsApi.upload(assetType, selectedFile, newAssetName, newAssetLabel, sizeMeters);
      await fetchAssets();
      resetForm();
      setIsAddFormVisible(false);
      refreshAssets();
    } catch (err) {
      if (err instanceof ApiError) {
        setUploadError(err.message);
      } else {
        setUploadError('Failed to upload asset');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (assetName: string): Promise<void> => {
    try {
      await assetsApi.delete(assetType, assetName);
      setAssets(assets.filter((a) => a.name !== assetName));
      refreshAssets();
    } catch (err) {
      console.error('Failed to delete asset:', err);
    }
  };

  const handleCancelAdd = (): void => {
    resetForm();
    setIsAddFormVisible(false);
  };

  // Generate unique folder name
  const generateUniqueFolderName = (parentPath: string | null): string => {
    const existingNames = new Set<string>();
    assets.forEach(asset => {
      if (asset.folder) {
        if (parentPath === null) {
          // Root level - get first segment
          const firstSegment = asset.folder.split('/')[0];
          existingNames.add(firstSegment);
        } else if (asset.folder.startsWith(parentPath + '/')) {
          // Child level - get next segment after parent
          const remaining = asset.folder.slice(parentPath.length + 1);
          const nextSegment = remaining.split('/')[0];
          if (nextSegment) existingNames.add(nextSegment);
        }
      }
    });

    let counter = 1;
    let name = 'New Folder';
    while (existingNames.has(name)) {
      counter++;
      name = `New Folder ${counter}`;
    }
    return name;
  };

  // Create new folder by creating a placeholder asset path
  const handleCreateFolder = (): void => {
    const folderName = generateUniqueFolderName(null);
    const newPath = folderName;

    // Create a temporary "folder" by adding it to open folders
    // The folder will persist when an asset is moved into it
    const newOpenFolders = new Set(openFolders);
    newOpenFolders.add(newPath);
    saveOpenFolders(newOpenFolders);

    // Start editing the folder name
    setEditingFolderPath(newPath);
    setEditingFolderName(folderName);
    setTimeout(() => folderInputRef.current?.select(), 0);
  };

  // Create subfolder
  const handleCreateSubfolder = (parentPath: string): void => {
    const folderName = generateUniqueFolderName(parentPath);
    const newPath = `${parentPath}/${folderName}`;

    const newOpenFolders = new Set(openFolders);
    newOpenFolders.add(parentPath); // Ensure parent is open
    newOpenFolders.add(newPath);
    saveOpenFolders(newOpenFolders);

    setEditingFolderPath(newPath);
    setEditingFolderName(folderName);
    setTimeout(() => folderInputRef.current?.select(), 0);
  };

  const handleFolderNameChange = (value: string): void => {
    // Prevent slashes in folder names
    setEditingFolderName(value.replace(/\//g, ''));
  };

  const handleFolderNameSubmit = async (): Promise<void> => {
    if (!editingFolderPath) return;
    const trimmedName = editingFolderName.trim();
    if (!trimmedName) {
      setEditingFolderPath(null);
      setEditingFolderName('');
      return;
    }

    const oldPath = editingFolderPath;
    const pathParts = oldPath.split('/');
    pathParts[pathParts.length - 1] = trimmedName;
    const newPath = pathParts.join('/');

    if (oldPath !== newPath) {
      // Update all assets that have this folder path or are in subfolders
      const assetsToUpdate = assets.filter(a =>
        a.folder === oldPath || (a.folder && a.folder.startsWith(oldPath + '/'))
      );

      for (const asset of assetsToUpdate) {
        const updatedFolder = asset.folder === oldPath
          ? newPath
          : newPath + asset.folder!.slice(oldPath.length);
        await updateAssetFolder(asset.name, updatedFolder);
      }

      // Update open folders
      const newOpenFolders = new Set<string>();
      openFolders.forEach(path => {
        if (path === oldPath) {
          newOpenFolders.add(newPath);
        } else if (path.startsWith(oldPath + '/')) {
          newOpenFolders.add(newPath + path.slice(oldPath.length));
        } else {
          newOpenFolders.add(path);
        }
      });
      saveOpenFolders(newOpenFolders);
    }

    setEditingFolderPath(null);
    setEditingFolderName('');
  };

  const handleFolderNameKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFolderNameSubmit();
    } else if (e.key === 'Escape') {
      setEditingFolderPath(null);
      setEditingFolderName('');
    }
  };

  const toggleFolder = (folderPath: string): void => {
    const newOpenFolders = new Set(openFolders);
    if (newOpenFolders.has(folderPath)) {
      newOpenFolders.delete(folderPath);
    } else {
      newOpenFolders.add(folderPath);
    }
    saveOpenFolders(newOpenFolders);
  };

  const deleteFolder = async (folderPath: string): Promise<void> => {
    // Move all assets in this folder (and subfolders) to root
    const assetsToUpdate = assets.filter(a =>
      a.folder === folderPath || (a.folder && a.folder.startsWith(folderPath + '/'))
    );

    for (const asset of assetsToUpdate) {
      await updateAssetFolder(asset.name, null);
    }

    // Remove from open folders
    const newOpenFolders = new Set<string>();
    openFolders.forEach(path => {
      if (path !== folderPath && !path.startsWith(folderPath + '/')) {
        newOpenFolders.add(path);
      }
    });
    saveOpenFolders(newOpenFolders);
  };

  // Update asset folder via API
  const updateAssetFolder = async (assetName: string, folderPath: string | null): Promise<void> => {
    try {
      await assetsApi.update(assetType, assetName, { folder: folderPath });
      setAssets(prev => prev.map(a => a.name === assetName ? { ...a, folder: folderPath } : a));
    } catch (err) {
      console.error('Failed to update asset folder:', err);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (type: 'asset' | 'folder', id: string, sourcePath: string | null): void => {
    setDragState({ type, id, sourcePath });
  };

  const handleDragEnd = (): void => {
    setDragState({ type: null, id: null, sourcePath: null });
    setDropTarget({ type: null, path: null });
  };

  const handleDragOver = (e: React.DragEvent, targetType: 'folder' | 'root' | 'hotbar-slot', targetPath: string | null, slotIndex?: number): void => {
    e.preventDefault();
    e.stopPropagation();

    // Only allow assets to be dropped on hotbar slots
    if (targetType === 'hotbar-slot' && dragState.type !== 'asset') {
      return;
    }

    // Don't allow dropping folder into itself or its descendants
    if (dragState.type === 'folder' && dragState.id) {
      if (targetType === 'folder' && targetPath) {
        if (targetPath === dragState.id || targetPath.startsWith(dragState.id + '/')) {
          return;
        }
      }
    }

    setDropTarget({ type: targetType, path: targetPath, slotIndex });
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDropTarget({ type: null, path: null });
    }
  };

  const handleHotbarDrop = (e: React.DragEvent, presetIndex: number, slotIndex: number): void => {
    e.preventDefault();
    e.stopPropagation();

    if (dragState.type !== 'asset' || !dragState.id) {
      handleDragEnd();
      return;
    }

    // Add asset to hotbar slot
    const newPresets = setHotbarSlot(assetType, presetIndex, slotIndex, dragState.id);
    setHotbarPresets(newPresets);
    // Note: Tool panels (BrushPanel, PropPanel) will pick up hotbar changes
    // when they switch presets or on next assetsVersion change
    handleDragEnd();
  };

  const handleHotbarSlotClear = (presetIndex: number, slotIndex: number): void => {
    const preset = hotbarPresets.presets[presetIndex];
    const currentAsset = preset?.slots[slotIndex];
    if (currentAsset) {
      const newPresets = removeFromHotbar(assetType, presetIndex, currentAsset);
      setHotbarPresets(newPresets);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetType: 'folder' | 'root', targetPath: string | null): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    if (!dragState.type || !dragState.id) return;

    // Don't drop on same location
    if (dragState.type === 'asset') {
      const asset = assets.find(a => a.name === dragState.id);
      const assetFolder = asset?.folder || null;
      if (assetFolder === targetPath) {
        handleDragEnd();
        return;
      }
      await updateAssetFolder(dragState.id, targetPath);
    } else if (dragState.type === 'folder') {
      const sourceFolderPath = dragState.id;

      // Don't drop folder into itself or descendants
      if (targetType === 'folder' && targetPath) {
        if (targetPath === sourceFolderPath || targetPath.startsWith(sourceFolderPath + '/')) {
          handleDragEnd();
          return;
        }
      }

      // Calculate new path for the folder
      const folderName = sourceFolderPath.split('/').pop() || sourceFolderPath;
      const newFolderPath = targetPath ? `${targetPath}/${folderName}` : folderName;

      // Don't drop on same parent
      const sourceParent = sourceFolderPath.includes('/')
        ? sourceFolderPath.substring(0, sourceFolderPath.lastIndexOf('/'))
        : null;
      if (sourceParent === targetPath) {
        handleDragEnd();
        return;
      }

      // Update all assets in the source folder
      const assetsToUpdate = assets.filter(a =>
        a.folder === sourceFolderPath || (a.folder && a.folder.startsWith(sourceFolderPath + '/'))
      );

      for (const asset of assetsToUpdate) {
        const newAssetFolder = asset.folder === sourceFolderPath
          ? newFolderPath
          : newFolderPath + asset.folder!.slice(sourceFolderPath.length);
        await updateAssetFolder(asset.name, newAssetFolder);
      }

      // Update open folders
      const newOpenFolders = new Set<string>();
      openFolders.forEach(path => {
        if (path === sourceFolderPath) {
          newOpenFolders.add(newFolderPath);
        } else if (path.startsWith(sourceFolderPath + '/')) {
          newOpenFolders.add(newFolderPath + path.slice(sourceFolderPath.length));
        } else {
          newOpenFolders.add(path);
        }
      });
      if (targetPath) {
        newOpenFolders.add(targetPath); // Open target folder
      }
      saveOpenFolders(newOpenFolders);
    }

    handleDragEnd();
  };

  // Computed data
  const getFolderStats = useCallback((folderPath: string): { assetCount: number; subfolderCount: number } => {
    const assetsInFolder = assets.filter(a =>
      a.folder === folderPath || (a.folder && a.folder.startsWith(folderPath + '/'))
    );

    // Count direct subfolders
    const directSubfolders = new Set<string>();
    assets.forEach(a => {
      if (a.folder && a.folder.startsWith(folderPath + '/')) {
        const remaining = a.folder.slice(folderPath.length + 1);
        const nextSegment = remaining.split('/')[0];
        if (nextSegment) {
          directSubfolders.add(nextSegment);
        }
      }
    });

    return {
      assetCount: assetsInFolder.length,
      subfolderCount: directSubfolders.size
    };
  }, [assets]);

  const rootAssets = useMemo(() => assets.filter(a => !a.folder), [assets]);

  // Render folder recursively
  const renderFolder = (folder: FolderNode, depth: number = 0): JSX.Element => {
    const folderAssets = assets.filter(a => a.folder === folder.path);
    const stats = getFolderStats(folder.path);
    const isDropTargetFolder = dropTarget.type === 'folder' && dropTarget.path === folder.path;
    const isDragging = dragState.type === 'folder' && dragState.id === folder.path;
    const isEditing = editingFolderPath === folder.path;

    return (
      <div key={folder.path} className="asset-folder" style={{ marginLeft: depth * 16 }}>
        <div
          className={`asset-folder-header ${isDropTargetFolder ? 'asset-folder-header--drop-target' : ''} ${isDragging ? 'asset-folder-header--dragging' : ''}`}
          draggable={!isEditing}
          onDragStart={(e) => {
            if (isEditing) return;
            e.stopPropagation();
            handleDragStart('folder', folder.path, folder.path.includes('/') ? folder.path.substring(0, folder.path.lastIndexOf('/')) : null);
          }}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, 'folder', folder.path)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'folder', folder.path)}
        >
          <button
            className="asset-folder-toggle"
            onClick={() => toggleFolder(folder.path)}
          >
            <i className={`fas fa-chevron-${folder.isOpen ? 'down' : 'right'}`}></i>
          </button>
          <i className={`fas fa-folder${folder.isOpen ? '-open' : ''} asset-folder-icon`}></i>
          {isEditing ? (
            <input
              ref={folderInputRef}
              className="asset-folder-name-input"
              type="text"
              value={editingFolderName}
              onChange={(e) => handleFolderNameChange(e.target.value)}
              onBlur={handleFolderNameSubmit}
              onKeyDown={handleFolderNameKeyDown}
              autoFocus
            />
          ) : (
            <span
              className="asset-folder-name"
              onDoubleClick={() => {
                setEditingFolderPath(folder.path);
                setEditingFolderName(folder.name);
              }}
            >
              {folder.name}
            </span>
          )}
          <span className="asset-folder-stats">
            {stats.assetCount > 0 && <span className="asset-folder-stat">{stats.assetCount}</span>}
            {stats.subfolderCount > 0 && <span className="asset-folder-stat"><i className="fas fa-folder"></i> {stats.subfolderCount}</span>}
          </span>
          <button
            className="asset-folder-add-subfolder"
            onClick={(e) => {
              e.stopPropagation();
              handleCreateSubfolder(folder.path);
            }}
            title="Create subfolder"
          >
            <i className="fas fa-folder-plus"></i>
          </button>
          <button
            className="asset-folder-delete"
            onClick={(e) => {
              e.stopPropagation();
              deleteFolder(folder.path);
            }}
            title="Delete folder"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
        {folder.isOpen && (
          <div className="asset-folder-content">
            {folder.children.map(child => renderFolder(child, depth + 1))}
            {folderAssets.map(asset => renderAssetItem(asset, depth + 1))}
            {folder.children.length === 0 && folderAssets.length === 0 && (
              <div className="asset-folder-empty" style={{ marginLeft: (depth + 1) * 16 }}>
                Empty folder
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render asset item
  const renderAssetItem = (asset: Asset, depth: number = 0): JSX.Element => {
    const isDragging = dragState.type === 'asset' && dragState.id === asset.name;

    return (
      <div
        key={asset.name}
        className={`asset-tab-item ${isDragging ? 'asset-tab-item--dragging' : ''}`}
        style={{ marginLeft: depth * 16 }}
        draggable
        onDragStart={(e) => {
          e.stopPropagation();
          handleDragStart('asset', asset.name, asset.folder || null);
        }}
        onDragEnd={handleDragEnd}
      >
        <div className="asset-tab-item-preview">
          <img src={assetsApi.getFileUrl(assetType, asset.name)} alt={asset.label} />
        </div>
        <div className="asset-tab-item-info">
          <span className="asset-tab-item-label">{asset.label}</span>
        </div>
        <div className="asset-tab-item-filename">
          <span>{asset.name}</span>
        </div>
        <div className="asset-tab-item-actions">
          <button
            className="asset-tab-item-delete"
            onClick={() => handleDelete(asset.name)}
            title="Delete asset"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="asset-tab-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Loading assets...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="asset-tab-error">
        <i className="fas fa-exclamation-triangle"></i>
        <span>{error}</span>
        <button onClick={fetchAssets}>Retry</button>
      </div>
    );
  }

  const isRootDropTarget = dropTarget.type === 'root';

  // Check if loaded data matches current assetType to prevent rendering stale data
  const isDataStale = loadedAssetType !== assetType;

  // Get asset data for each preset's slots
  // Returns empty arrays when loading or data is stale to prevent rendering with wrong assetType
  const getPresetAssets = (presetIndex: number): (Asset | null)[] => {
    // When loading or data is stale, return empty slots to prevent rendering stale assets with wrong assetType
    if (isLoading || isDataStale) return Array(HOTBAR_SIZE).fill(null);
    const preset = hotbarPresets.presets[presetIndex];
    if (!preset) return Array(HOTBAR_SIZE).fill(null);
    return preset.slots.map((name: string | null) => {
      if (!name) return null;
      return assets.find(a => a.name === name) || null;
    });
  };

  return (
    <div className="asset-tab">
      {/* Hotbar Presets Configuration */}
      <div className="asset-tab-hotbar">
        <div className="asset-tab-hotbar-title">
          <i className="fas fa-th"></i>
          <span>Quick Access Presets</span>
        </div>
        <div className="asset-tab-hotbar-presets">
          {Array.from({ length: HOTBAR_PRESET_COUNT }).map((_, presetIndex) => {
            const presetAssets = getPresetAssets(presetIndex);
            return (
              <div key={presetIndex} className="asset-tab-hotbar-preset">
                <div className="asset-tab-hotbar-preset-label">{presetIndex + 1}</div>
                <div className="asset-tab-hotbar-grid">
                  {presetAssets.map((asset: Asset | null, slotIndex: number) => {
                    const isSlotDropTarget = dropTarget.type === 'hotbar-slot'
                      && dropTarget.presetIndex === presetIndex
                      && dropTarget.slotIndex === slotIndex;
                    return (
                      <div
                        key={slotIndex}
                        className={`asset-tab-hotbar-slot ${isSlotDropTarget ? 'asset-tab-hotbar-slot--drop-target' : ''} ${asset ? '' : 'asset-tab-hotbar-slot--empty'}`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (dragState.type === 'asset') {
                            setDropTarget({ type: 'hotbar-slot', path: null, presetIndex, slotIndex });
                          }
                        }}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleHotbarDrop(e, presetIndex, slotIndex)}
                      >
                        {asset ? (
                          <>
                            <img
                              src={assetsApi.getFileUrl(assetType, asset.name)}
                              alt={asset.label}
                              title={asset.label}
                            />
                            <button
                              className="asset-tab-hotbar-slot-clear"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHotbarSlotClear(presetIndex, slotIndex);
                              }}
                              title="Remove from quick access"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        ) : (
                          <span className="asset-tab-hotbar-slot-placeholder"></span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="asset-tab-hotbar-hint">
          Drag assets to configure presets
        </div>
      </div>

      <div
        className={`asset-tab-list ${isRootDropTarget ? 'asset-tab-list--drop-target' : ''}`}
        onDragOver={(e) => handleDragOver(e, 'root', null)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 'root', null)}
      >
        {isDataStale ? (
          <div className="asset-tab-loading">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading assets...</span>
          </div>
        ) : assets.length === 0 && folderTree.length === 0 && !isAddFormVisible ? (
          <div className="asset-tab-list-empty">
            <span>No assets yet</span>
            <span className="asset-tab-list-empty-hint">Click + to add assets or folder icon to create folders</span>
          </div>
        ) : (
          <div className="asset-tab-list-items">
            {folderTree.map(folder => renderFolder(folder))}
            {rootAssets.map(asset => renderAssetItem(asset))}
          </div>
        )}
      </div>

      {/* Add Form Overlay */}
      {isAddFormVisible && (
        <div className="asset-tab-add-form">
          <div className="asset-tab-add-form-header">
            <span className="asset-tab-add-form-title">Add New Asset</span>
            <button className="asset-tab-add-form-close" onClick={handleCancelAdd}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          {uploadError && <div className="asset-tab-add-form-error">{uploadError}</div>}

          <div className="asset-tab-add-form-fields">
            <div className="asset-tab-add-form-field">
              <label>File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFileSelect}
                disabled={isUploading}
              />
            </div>

            <div className="asset-tab-add-form-field">
              <label>Label</label>
              <input
                type="text"
                placeholder="My Asset"
                value={newAssetLabel}
                onChange={(e) => handleLabelChange(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <div className="asset-tab-add-form-field">
              <label>Name (ID)</label>
              <input
                type="text"
                placeholder="my_asset"
                value={newAssetName}
                onChange={(e) => handleNameChange(e.target.value)}
                disabled={isUploading}
              />
            </div>

            {assetType === 'props' && (
              <div className="asset-tab-add-form-field">
                <label>Size (meters)</label>
                <input
                  type="number"
                  placeholder="1"
                  value={newAssetSize}
                  onChange={(e) => setNewAssetSize(e.target.value)}
                  disabled={isUploading}
                  step="0.1"
                  min="0.01"
                />
              </div>
            )}
          </div>

          <div className="asset-tab-add-form-actions">
            <button
              className="asset-tab-add-form-cancel"
              onClick={handleCancelAdd}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              className="asset-tab-add-form-submit"
              onClick={handleUpload}
              disabled={isUploading || !selectedFile || !newAssetName || !newAssetLabel}
            >
              {isUploading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-upload"></i>
                  <span>Upload</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Floating Action Buttons */}
      {!isAddFormVisible && (
        <div className="asset-tab-fab-container">
          <button
            className="asset-tab-fab asset-tab-fab-folder"
            onClick={handleCreateFolder}
            title="Create new folder"
          >
            <i className="fas fa-folder-plus"></i>
          </button>
          <button
            className="asset-tab-fab asset-tab-fab-add"
            onClick={() => setIsAddFormVisible(true)}
            title="Add new asset"
          >
            <i className="fas fa-plus"></i>
          </button>
        </div>
      )}
    </div>
  );
}

export default AssetTab;
